/* ==========================================================================
   FLC Forge — Collection Filter & Sort
   ========================================================================== */

class FilterDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer   = this.querySelector('[data-filter-inner]');
    this.openBtn  = document.querySelector('[data-filter-open]');
    this.closeBtn = this.querySelector('[data-filter-close]');
    this.clearBtn = this.querySelector('[data-filter-clear]');
    this.applyBtn = this.querySelector('[data-filter-apply]');
    this.form     = this.querySelector('[data-filter-form]') || document.querySelector('[data-filter-form]');

    this.openBtn?.addEventListener('click',  () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.clearBtn?.addEventListener('click', () => this.clearFilters());

    this.applyBtn?.addEventListener('click', async () => {
      await this.submitFilter();
      this.close();
    });

    document.addEventListener('flc:drawers:close-all', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // Auto-apply while drawer is open so count + active filters update in real time
    this.form?.addEventListener('change', FLC.debounce(() => this.submitFilter(), 400));
  }

  get sectionHandle() {
    const el = document.querySelector('[data-section-handle="main-collection"], [data-section-handle="main-search"]');
    return el?.dataset.sectionId || 'main-collection';
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    this.drawer?.classList.add('is-open');
    FLC.openOverlay('filter-drawer');
    FLC.trapFocus(this);
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.drawer?.classList.remove('is-open');
    FLC.closeOverlay('filter-drawer');
  }

  async clearFilters() {
    this.form?.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
    this.form?.querySelectorAll('input[type="number"]').forEach(n => { n.value = ''; });
    await this.submitFilter();
    this.close();
  }

  async submitFilter(overrideUrl) {
    const searchParams = new URLSearchParams(new FormData(this.form)).toString();
    const url = overrideUrl || `${window.location.pathname}?${searchParams}`;
    this.setLoading(true);

    try {
      const separator = url.includes('?') ? '&' : '?';
      const res  = await fetch(`${url}${separator}sections=${this.sectionHandle}`);
      const data = await res.json();
      const html = data[this.sectionHandle];

      if (html) {
        const parser = new DOMParser();
        const doc    = parser.parseFromString(html, 'text/html');
        this.replaceSection(doc);
        this.updateURL(url);
        this.syncFormToURL(url);
        this.refreshLoadMore(url);
      }
    } catch (err) {
      console.error('Filter failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  syncFormToURL(url) {
    if (!this.form) return;
    const params = new URLSearchParams(new URL(url, window.location.origin).search);

    this.form.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      const active = params.getAll(cb.name).includes(cb.value);
      cb.checked = active;
      cb.closest('.filter-group__label')?.classList.toggle('is-active', active);
    });

    this.form.querySelectorAll('input[type="number"]').forEach(input => {
      input.value = params.get(input.name) ?? '';
    });
  }

  refreshLoadMore(filterUrl) {
    const wrapper = document.querySelector('[data-load-more-wrapper]');
    if (!wrapper) return;

    // Use the page size from the initial full-page render (avoids section-rendering
    // default-value mismatch where Shopify returns 24 instead of the merchant's setting)
    const pageSize = parseInt(wrapper.dataset.pageSize || '0', 10);

    // Total from the product count badge, e.g. "(25)" → 25
    const countEl = document.querySelector('[data-product-count]');
    const total   = parseInt((countEl?.textContent || '').replace(/\D/g, ''), 10) || 0;

    const viewed  = Math.min(pageSize, total);

    if (total === 0) { wrapper.remove(); return; }

    let inner = `<p class="load-more__count">You've viewed <span data-viewed-count>${viewed}</span> of ${total} products</p>`;

    if (viewed < total) {
      const nextUrl = new URL(filterUrl, location.href);
      nextUrl.searchParams.set('page', '2');
      inner += `<load-more data-next-url="${nextUrl.pathname + nextUrl.search}" data-viewed="${viewed}" data-total="${total}">
        <button class="load-more__btn button button--secondary" type="button">VIEW MORE</button>
      </load-more>`;
    }

    wrapper.innerHTML = inner;
  }

  replaceSection(doc) {
    const newGrid    = doc.querySelector('[data-product-grid]');
    const newFilters = doc.querySelector('[data-active-filters]');
    const newCount   = doc.querySelector('[data-product-count]');
    const newBadge   = doc.querySelector('[data-filter-badge]');

    // Product grid
    const liveGrid = document.querySelector('[data-product-grid]');
    if (liveGrid && newGrid) liveGrid.replaceWith(newGrid);

    // Active filters — insert on first apply, replace, or remove when cleared
    const liveFilters = document.querySelector('[data-active-filters]');
    if (newFilters && liveFilters) {
      liveFilters.replaceWith(newFilters);
    } else if (newFilters && !liveFilters) {
      document.querySelector('.collection-toolbar__left')?.appendChild(newFilters);
    } else if (!newFilters && liveFilters) {
      liveFilters.remove();
    }

    // Filter badge on the toolbar button
    const liveBadge = document.querySelector('[data-filter-badge]');
    const openBtn   = document.querySelector('[data-filter-open]');
    if (newBadge && !liveBadge && openBtn) {
      openBtn.appendChild(newBadge);
    } else if (!newBadge && liveBadge) {
      liveBadge.remove();
    }

    // Product count
    const liveCount = document.querySelector('[data-product-count]');
    if (liveCount && newCount) liveCount.replaceWith(newCount);
  }

  updateURL(url) {
    window.history.pushState({}, '', url);
    if (window.flcForge?.clarityVirtualPageFix && typeof window.clarity === 'function') {
      window.clarity('set', 'virtualPageView', window.location.pathname + window.location.search);
    }
  }

  setLoading(state) {
    document.querySelector('[data-product-grid]')?.classList.toggle('is-loading', state);
  }
}

customElements.define('filter-drawer', FilterDrawer);

/* Sort dropdown
   ========================================================================== */
document.querySelectorAll('[data-sort-dropdown]').forEach(el => {
  const trigger = el.querySelector('.sort-btn__trigger');
  const menu    = el.querySelector('.sort-btn__menu');
  if (!trigger || !menu) return;

  const options = () => [...menu.querySelectorAll('.sort-btn__option')];

  function open() {
    trigger.setAttribute('aria-expanded', 'true');
    menu.removeAttribute('hidden');
    const current = menu.querySelector('.is-selected') || options()[0];
    current?.focus();
  }

  function close() {
    trigger.setAttribute('aria-expanded', 'false');
    menu.setAttribute('hidden', '');
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    trigger.getAttribute('aria-expanded') === 'true' ? close() : open();
  });

  menu.addEventListener('click', (e) => {
    const opt = e.target.closest('.sort-btn__option');
    if (!opt) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.set('sort_by', opt.dataset.value);
    window.location.href = url.toString();
  });

  menu.addEventListener('keydown', (e) => {
    const opts = options();
    const idx  = opts.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); opts[Math.min(idx + 1, opts.length - 1)]?.focus(); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); opts[Math.max(idx - 1, 0)]?.focus(); }
    if (e.key === 'Enter')     { document.activeElement.click(); }
    if (e.key === 'Escape')    { close(); trigger.focus(); }
  });

  document.addEventListener('click', (e) => { if (!el.contains(e.target)) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
});

/* Active filter removal — instant via section rendering
   ========================================================================== */
document.addEventListener('click', (e) => {
  const removeBtn = e.target.closest('[data-remove-filter]');
  if (!removeBtn) return;
  e.preventDefault();

  const drawer = document.querySelector('filter-drawer');
  if (drawer) {
    drawer.submitFilter(new URL(removeBtn.href).toString());
  } else {
    window.location.href = removeBtn.href;
  }
});

/* Load more button
   ========================================================================== */
class LoadMore extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('.load-more__btn');
    this.btn?.addEventListener('click', () => this.loadMore());
  }

  async loadMore() {
    const url    = this.dataset.nextUrl;
    const viewed = parseInt(this.dataset.viewed, 10);
    const total  = parseInt(this.dataset.total, 10);
    if (!url) return;

    this.btn.disabled = true;

    try {
      // Build fetch URL using URL API so relative paths resolve correctly
      const sectionId = document.querySelector('[data-section-handle="main-collection"], [data-section-handle="main-search"]')?.dataset.sectionId || 'main-collection';
      const fetchUrl  = new URL(url, location.href);
      fetchUrl.searchParams.set('sections', sectionId);

      const res  = await fetch(fetchUrl.toString());
      const data = await res.json();
      const html = data[sectionId];
      if (!html) throw new Error('No section HTML');

      const parser = new DOMParser();
      const doc    = parser.parseFromString(html, 'text/html');
      const grid   = document.querySelector('[data-product-grid]');

      // Append all cards (products + disruptors); count only real products
      const allCards     = [...doc.querySelectorAll('[data-product-card]')];
      const productCount = allCards.filter(el => !el.classList.contains('product-grid__disruptor')).length;
      allCards.forEach(item => grid?.appendChild(item));

      // Update viewed count — derived locally, no reliance on section rendering paginate
      const newViewed = Math.min(viewed + productCount, total);
      const viewedEl  = document.querySelector('[data-viewed-count]');
      if (viewedEl) viewedEl.textContent = newViewed;
      this.dataset.viewed = newViewed;

      if (newViewed < total) {
        // More pages remain — increment the page param in the current URL
        const nextUrl = new URL(url, location.href);
        nextUrl.searchParams.set('page', String(parseInt(nextUrl.searchParams.get('page') || '1', 10) + 1));
        this.dataset.nextUrl = nextUrl.pathname + nextUrl.search;
        this.btn.disabled = false;
      } else {
        // All products loaded — remove button, keep the "You've viewed" counter
        this.remove();
      }

      // Update URL so a refresh shows the same page
      history.replaceState({}, '', url);

    } catch (err) {
      console.error('Load more failed:', err);
      this.btn.disabled = false;
    }
  }
}

customElements.define('load-more', LoadMore);
